import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { aiAPI, locationAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import {
  Compass,
  MapPin,
  Loader2,
  Coffee,
  Mountain,
  Utensils,
  Music,
  Camera,
  Bike,
  Wine,
  Castle,
  TreePine,
  Users,
  Search,
  ArrowLeft,
  RefreshCw,
  Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LocalFavoritesPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [locations, setLocations] = useState([]);
  const [formData, setFormData] = useState({
    location: '',
    customLocation: '',
    interests: [],
    crowd_preference: 'low',
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await locationAPI.getAll();
      // API returns array directly, not wrapped in { locations: [...] }
      const locationsData = Array.isArray(response.data) ? response.data : (response.data.locations || []);
      setLocations(locationsData);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  const interestOptions = [
    { id: 'cafes', label: 'Cozy Cafés', icon: Coffee },
    { id: 'hiking', label: 'Hiking Trails', icon: Mountain },
    { id: 'food', label: 'Local Food', icon: Utensils },
    { id: 'music', label: 'Live Music', icon: Music },
    { id: 'photography', label: 'Photo Spots', icon: Camera },
    { id: 'cycling', label: 'Cycling Routes', icon: Bike },
    { id: 'wine', label: 'Wine & Beer', icon: Wine },
    { id: 'castles', label: 'Castles & History', icon: Castle },
    { id: 'nature', label: 'Nature & Parks', icon: TreePine },
    { id: 'family', label: 'Family Activities', icon: Users },
  ];

  const toggleInterest = (id) => {
    setFormData((prev) => ({
      ...prev,
      interests: prev.interests.includes(id)
        ? prev.interests.filter((i) => i !== id)
        : [...prev.interests, id],
    }));
  };

  const getLocationName = () => {
    if (formData.customLocation) return formData.customLocation;
    if (formData.location) {
      const loc = locations.find(l => l.id === formData.location);
      return loc?.name || formData.location;
    }
    return '';
  };

  const handleSubmit = async () => {
    const locationName = getLocationName();
    if (!locationName) return;

    setLoading(true);
    try {
      const response = await aiAPI.hiddenGems({
        location: locationName,
        interests: formData.interests,
        crowd_preference: formData.crowd_preference,
      });
      setResults({
        ...response.data,
        searchedLocation: locationName,
        searchedInterests: formData.interests
      });
    } catch (error) {
      console.error('AI Error:', error);
      setResults({
        response: "I'm having trouble finding hidden gems right now. Please try again in a moment.",
        error: true,
        searchedLocation: locationName
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNewSearch = () => {
    setResults(null);
    setFormData(prev => ({ ...prev, interests: [] }));
  };

  return (
    <div className="min-h-screen bg-slate-100 pt-20" data-testid="local-favorites-page">
      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1539665315126-4cadf53fb20d?w=1920&h=600&fit=crop"
            alt="German castle"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-ivory/80 via-champagne-100/85 to-ivory" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-military-gold/10 border border-military-gold/30 rounded-full mb-4">
            <Compass className="w-4 h-4 text-military-gold" />
            <span className="text-military-gold text-sm">{t('local_favorites.badge', 'Discover Like a Local')}</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-slate-900 mb-4 tracking-tight">
            {t('local_favorites.title', 'LOCAL FAVORITES')}
          </h1>
          <p className="text-slate-700 text-lg max-w-2xl mx-auto">
            {t('local_favorites.subtitle', 'Discover the places locals love - from cozy cafes to scenic spots that most visitors never find.')}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        {!results ? (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 sm:p-8">
            {/* Location Input - Required */}
            <div className="mb-8">
              <h2 className="font-heading text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-signal-blue" />
                WHERE ARE YOU EXPLORING?
              </h2>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-800 mb-2">{t('local_favorites.select_location', 'Select a location')}</p>
                  <Select
                    value={formData.location}
                    onValueChange={(value) => setFormData({ ...formData, location: value, customLocation: '' })}
                  >
                    <SelectTrigger className="bg-white border-slate-300 text-slate-800" data-testid="location-select">
                      <SelectValue placeholder="Choose a location..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-300 max-h-60">
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id} className="text-slate-700">
                          {loc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <p className="text-xs text-slate-800 mb-2">Or enter any location</p>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-800" />
                    <Input
                      value={formData.customLocation}
                      onChange={(e) => setFormData({ ...formData, customLocation: e.target.value, location: '' })}
                      placeholder="e.g., Heidelberg, Munich, Stuttgart..."
                      className="pl-10 bg-white border-slate-300 text-slate-800 placeholder:text-slate-800"
                      data-testid="custom-location"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Interests Grid */}
            <div className="mb-8">
              <h3 className="font-heading text-lg font-bold text-slate-800 mb-4">
                WHAT ARE YOU LOOKING FOR?
              </h3>
              <p className="text-slate-600 text-sm mb-4">Select your interests (optional - select multiple):</p>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {interestOptions.map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => toggleInterest(id)}
                    className={`p-3 rounded-lg border text-center transition-all ${
                      formData.interests.includes(id)
                        ? 'bg-military-gold/10 border-military-gold text-military-gold'
                        : 'bg-white border-slate-300 text-slate-600 hover:border-slate-600'
                    }`}
                    data-testid={`interest-${id}`}
                  >
                    <Icon className="w-5 h-5 mx-auto mb-1.5" />
                    <span className="text-xs">{label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Crowd Preference */}
            <div className="mb-8">
              <p className="text-sm text-slate-600 mb-2">Crowd Preference</p>
              <Select
                value={formData.crowd_preference}
                onValueChange={(value) => setFormData({ ...formData, crowd_preference: value })}
              >
                <SelectTrigger className="bg-white border-slate-300 text-slate-800 max-w-xs" data-testid="crowd-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-300">
                  <SelectItem value="low" className="text-slate-700">{t('local_favorites.crowd_low', 'Hidden & Quiet')}</SelectItem>
                  <SelectItem value="medium" className="text-slate-700">{t('local_favorites.crowd_medium', 'Some People OK')}</SelectItem>
                  <SelectItem value="high" className="text-slate-700">{t('local_favorites.crowd_high', "Don't Mind Crowds")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Submit Button */}
            <Button
              onClick={handleSubmit}
              disabled={loading || (!formData.location && !formData.customLocation)}
              className="w-full bg-military-gold hover:bg-military-gold/90 text-slate-950 rounded-lg py-5 font-bold"
              data-testid="discover-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Discovering Local Favorites...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Discover Local Favorites
                </>
              )}
            </Button>

            {!formData.location && !formData.customLocation && (
              <p className="text-xs text-slate-800 text-center mt-3">
                Enter a location to discover local favorites
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-6 animate-fade-in">
            {/* Results Header */}
            <div className="flex items-center justify-between">
              <button
                onClick={handleNewSearch}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-200 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                New Search
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex items-center gap-2 text-signal-blue hover:text-signal-blue-hover transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {/* Location Title */}
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-military-gold/10 border border-military-gold/30 rounded-full mb-3">
                <MapPin className="w-4 h-4 text-military-gold" />
                <span className="text-military-gold text-sm">{results.searchedLocation}</span>
              </div>
              <h2 className="font-heading text-2xl font-bold text-slate-800 mb-2">
                YOUR HIDDEN GEMS
              </h2>
              {results.searchedInterests?.length > 0 && (
                <p className="text-slate-600 text-sm">
                  Based on: {results.searchedInterests.map(i => 
                    interestOptions.find(opt => opt.id === i)?.label || i
                  ).join(', ')}
                </p>
              )}
            </div>

            {/* Results Card - Clean Formatting */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 sm:p-8">
              <div className="prose prose-sm max-w-none">
                <FormattedResponse content={results.response} />
              </div>
              
              {/* Images if available */}
              {results.images && results.images.length > 0 && (
                <div className="grid grid-cols-2 gap-4 mt-6">
                  {results.images.map((img, i) => (
                    <div key={i} className="rounded-lg overflow-hidden border border-slate-300">
                      <img 
                        src={img.url} 
                        alt={img.caption || 'Hidden gem'} 
                        className="w-full h-40 object-cover"
                      />
                      {img.caption && (
                        <p className="text-xs text-slate-800 p-2 bg-white/50">{img.caption}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {results.provider && (
                <p className="text-xs text-slate-800 mt-6 pt-4 border-t border-slate-300/50 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  Powered by {results.provider}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleNewSearch}
                className="flex-1 border-slate-300 text-slate-600"
              >
                Search Different Location
              </Button>
              <Button
                onClick={() => navigate('/concierge')}
                className="flex-1 bg-signal-blue hover:bg-signal-blue-hover text-white rounded-lg"
              >
                {t('local_favorites.ask_concierge_cta', 'Ask Concierge for More')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Component to format AI responses cleanly - same as Concierge
function FormattedResponse({ content }) {
  if (!content) return null;
  
  const lines = content.split('\n');
  const elements = [];
  let currentList = [];
  let inList = false;
  
  lines.forEach((line, index) => {
    const trimmed = line.trim();
    
    // Check for markdown headers
    if (trimmed.startsWith('### ')) {
      if (inList && currentList.length > 0) {
        elements.push(
          <ul key={`list-${index}`} className="my-3 space-y-2">
            {currentList.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <span className="text-military-gold mt-1">●</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        );
        currentList = [];
        inList = false;
      }
      elements.push(
        <h3 key={`h3-${index}`} className="font-bold text-lg text-military-gold mt-6 mb-3">
          {trimmed.replace('### ', '')}
        </h3>
      );
    }
    // Check for sub-headers
    else if (trimmed.startsWith('## ')) {
      elements.push(
        <h2 key={`h2-${index}`} className="font-bold text-xl text-slate-800 mt-6 mb-3">
          {trimmed.replace('## ', '')}
        </h2>
      );
    }
    // Check for bold headers (lines ending with :)
    else if (trimmed.endsWith(':') && trimmed.length < 60 && !trimmed.startsWith('-') && !trimmed.startsWith('•')) {
      if (inList && currentList.length > 0) {
        elements.push(
          <ul key={`list-${index}`} className="my-3 space-y-2">
            {currentList.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <span className="text-military-gold mt-1">●</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        );
        currentList = [];
        inList = false;
      }
      elements.push(
        <h4 key={`h4-${index}`} className="font-semibold text-slate-800 mt-5 mb-2">
          {trimmed}
        </h4>
      );
    }
    // Check for bullet points
    else if (trimmed.startsWith('•') || trimmed.startsWith('-') || trimmed.startsWith('*')) {
      if (!inList) {
        inList = true;
        currentList = [];
      }
      currentList.push(trimmed.replace(/^[•\-\*]\s*/, ''));
    }
    // Check for numbered items
    else if (/^\d+[\.\)]\s/.test(trimmed)) {
      if (inList && currentList.length > 0) {
        elements.push(
          <ul key={`list-${index}`} className="my-3 space-y-2">
            {currentList.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <span className="text-military-gold mt-1">●</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        );
        currentList = [];
        inList = false;
      }
      const match = trimmed.match(/^(\d+)[\.\)]\s*(.*)/);
      if (match) {
        elements.push(
          <div key={`num-${index}`} className="flex items-start gap-3 my-2">
            <span className="w-6 h-6 bg-signal-blue/20 text-signal-blue rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">
              {match[1]}
            </span>
            <p className="text-sm text-slate-700">{match[2]}</p>
          </div>
        );
      }
    }
    // Regular paragraph
    else if (trimmed) {
      if (inList && currentList.length > 0) {
        elements.push(
          <ul key={`list-${index}`} className="my-3 space-y-2">
            {currentList.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <span className="text-military-gold mt-1">●</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        );
        currentList = [];
        inList = false;
      }
      elements.push(
        <p key={`p-${index}`} className="text-sm text-slate-700 my-2 leading-relaxed">
          {trimmed}
        </p>
      );
    }
  });
  
  // Handle remaining list items
  if (currentList.length > 0) {
    elements.push(
      <ul key="list-final" className="my-3 space-y-2">
        {currentList.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
            <span className="text-military-gold mt-1">●</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  }
  
  return <div className="space-y-1">{elements}</div>;
}
